import { CARTO_TILES } from '@shared/tiles.js';
import { getServerBase } from './server';
import type { MapConfig } from './types';

/**
 * Woher kommen die Kartenkacheln - und wer kennt den Schlüssel dafür?
 *
 * Die Antwort kommt normalerweise vom Server (`/api/config` -> `map`, siehe
 * server/src/map.js). Zwei Dinge kommen hier dazu:
 *
 *  - Die Antwort wird gemerkt. Leaflet baut seine Kachel-Ebene synchron auf,
 *    lange bevor eine Netzanfrage zurück ist; ohne Gedächtnis flackerte beim
 *    Start jedes Mal kurz die Standardkarte durch.
 *  - Der Schlüssel darf lokal liegen. Sagt der Server `keyRequired`, kennt er
 *    selbst keinen - dann trägt man ihn in der Android-App in den
 *    Einstellungen ein, dort wo auch die Serveradresse steht.
 *
 * Kennt der Server dagegen einen Schlüssel, schickt er gar keine fremde
 * Adresse mehr, sondern seine eigene (`/api/tiles/...`) und holt die Kacheln
 * selbst - der Schlüssel bleibt dort. Diese Adresse ist relativ und braucht
 * in der Android-App die Serveradresse davor, genau wie jeder API-Pfad.
 */

/** Der Stand ohne jede Konfiguration: CARTO "Positron", ohne Schlüssel. */
export const KEYLESS_MAP: MapConfig = { ...CARTO_TILES, keyRequired: false };

const KEY_STORAGE = 'r2f.mapkey';
const CONFIG_STORAGE = 'r2f.mapconfig';

function readStoredConfig(): MapConfig | null {
  try {
    const raw = localStorage.getItem(CONFIG_STORAGE);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    return typeof parsed?.tileUrl === 'string' && parsed.tileUrl ? (parsed as MapConfig) : null;
  } catch {
    return null;
  }
}

let current: MapConfig = readStoredConfig() ?? KEYLESS_MAP;

/** Was der Server zuletzt gesagt hat - oder die schlüsselfreie Karte. */
export function mapConfig(): MapConfig {
  return current;
}

/** Die Antwort des Servers übernehmen. Ältere Server schicken kein `map`. */
export function rememberMapConfig(config: MapConfig | undefined | null): void {
  if (!config || typeof config.tileUrl !== 'string' || !config.tileUrl) return;
  current = config;
  try {
    localStorage.setItem(CONFIG_STORAGE, JSON.stringify(config));
  } catch {
    // Ohne Speicher gilt die Karte eben nur für diese Sitzung.
  }
}

/** Braucht die Karte einen Schlüssel, den der Server nicht kennt? */
export function mapKeyRequired(): boolean {
  return current.keyRequired === true;
}

export function getMapApiKey(): string {
  try {
    return localStorage.getItem(KEY_STORAGE) ?? '';
  } catch {
    return '';
  }
}

export function setMapApiKey(key: string): void {
  const value = key.trim();
  try {
    if (value) localStorage.setItem(KEY_STORAGE, value);
    else localStorage.removeItem(KEY_STORAGE);
  } catch {
    /* egal */
  }
}

/**
 * Die Kachel-Ebene, die tatsächlich geladen wird.
 *
 * Fehlt ein verlangter Schlüssel, gibt es die schlüsselfreie Karte statt
 * einer leeren Fläche - eine Karte ohne Schlüssel ist immer noch besser als
 * gar keine.
 */
export function activeMapConfig(): MapConfig {
  if (!mapKeyRequired()) return withServerBase(current);
  const key = getMapApiKey();
  if (!key) return KEYLESS_MAP;
  return withServerBase({
    ...current,
    tileUrl: current.tileUrl.replaceAll('{key}', encodeURIComponent(key)),
  });
}

/** Kacheln vom eigenen Server: relativer Pfad, in der App mit Adresse davor. */
function withServerBase(config: MapConfig): MapConfig {
  if (!config.tileUrl.startsWith('/')) return config;
  return { ...config, tileUrl: `${getServerBase()}${config.tileUrl}` };
}
